<template>
    <Tab :title="$trad.brand.navigation.access">
        <loader :loading="true" v-if="loading"></loader>
        <div class="row">
            <div class="col-12">
                <h2 class="uppercase">{{ $t("brandadmin.title") }} <span v-if="required">*</span></h2>
                <p :class="{ error }">{{ error }}</p>
                <ClientColorButton :size="'xs'" background-color="grey" class="buttonSelect" @click="updateGlobalValue();">
                    <template v-if="allApplicationsAreSelected">
                        {{ $t("common.selectNone") }}
                    </template>
                    <template v-else>
                        {{ $t("common.selectAll") }}
                    </template>
                </ClientColorButton>
            </div>
        </div>
        <section>
            <div v-for="app in applications" :key="app.value" class="checkbox">
                <label class="pointer label-checkbox d-flex align-items-center" :for="`brandAccess-${app.value}`">
                    <input
                        type="checkbox"
                        class="col-auto d-none"
                        :id="`brandAccess-${app.value}`"
                        :ref="`brandAccess-${app.value}`"
                        @input="e => selectValue(app.value, e)"
                        :checked="applicationSelected(app.value) && applicationActive(app.value)"
                    />
                    <span v-show="applicationSelected(app.value) && applicationActive(app.value)" class="rem3rch-checkbox rem3rch-checkbox-xs">
                        <i class="fa fa-check fa-xs"></i>
                    </span>
                    <span v-if="!applicationSelected(app.value) || !applicationActive(app.value)" class="rem3rch-checkbox rem3rch-checkbox-xs"></span>
                    <span class="uppercase app-title">{{ app.label }}</span>
                </label>
                <div v-if="applicationSelected(app.value) && applicationActive(app.value) && app.parameters.length > 0">
                    <div class="row">
                        <div class="col-2"></div>
                        <div class="col-10 container-parameters">
                            <label
                                v-bind:for="`app_${app.value}_param_${param.id}`"
                                v-for="param in app.parameters"
                                class="row label-checkbox parameter d-flex align-items-center mb-0 pointer"
                            >
                                <div class="col d-flex align-items-center">
                                    <span>{{ getTranslate(param.terms.name) }}</span>
                                </div>
                                <div class="col-auto" v-if="param.comment">
                                    <span class="small">{{ getTranslate(param.terms.comment) }}</span>
                                </div>
                                <div class="mr-5" v-bind:class="param.type === 'textarea' ? 'col-12 text-right' : 'col-auto'">
                                    <template v-if="param.type === 'boolean'">
                                        <input
                                            class="d-none"
                                            v-bind:id="`app_${app.value}_param_${param.id}`"
                                            v-bind:checked="getAppParamValue(app.value, param)"
                                            type="checkbox"
                                            @input="updateParamValue($event, app, param);"
                                        />
                                        <span v-show="getAppParamValue(app.value, param)" class="rem3rch-checkbox rem3rch-checkbox-xs">
                                            <i class="fa fa-check fa-xs"></i>
                                        </span>
                                        <span v-if="!getAppParamValue(app.value, param)" class="rem3rch-checkbox rem3rch-checkbox-xs"></span>
                                    </template>
                                    <input
                                        v-bind:id="`app_${app.value}_param_${param.id}`"
                                        v-if="param.type === 'number'"
                                        v-digitsonly="{ min: 1, max: 36 }"
                                        v-bind:value="getAppParamValue(app.value, param)"
                                        type="text"
                                        class="input-number"
                                        @input="updateParamValue($event, app, param);"
                                    />
                                    <input
                                        v-bind:id="`app_${app.value}_param_${param.id}`"
                                        v-if="param.type === 'text'"
                                        v-bind:value="getAppParamValue(app.value, param)"
                                        type="text"
                                        @input="updateParamValue($event, app, param);"
                                    />
                                    <textarea
                                        v-bind:id="`app_${app.value}_param_${param.id}`"
                                        v-if="param.type === 'textarea'"
                                        v-bind:value="getAppParamValue(app.value, param)"
                                        class="w-100 mt-2"
                                        @input="updateParamValue($event, app, param);"
                                    >
                                    </textarea>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </Tab>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import CheckboxGroupBloc from "@/components/ui/Form/CheckboxGroupBloc";
import loader from "@/components/loader/loader";
import TryLoadMixin from "@/mixins/TryLoadMixin";
import Tab from "@/components/admin/brand/tabs/Tab";
import Globals from "@/components/globals";
import Template from "@/plano/template";
import { JustDigits } from "@/utils/JustDigit";

const CATALOG = { id: 1 };

export default {
    props: {
        brand: {
            type: null
        },
        required: {
            type: Boolean,
            required: false,
            default: false
        },
        errorMessage: {
            type: String,
            required: false,
            default: undefined
        }
    },
    mixins: [TryLoadMixin],
    components: {
        Template,
        CheckboxGroupBloc,
        loader,
        Tab
    },
    model: {
        prop: "brand"
    },
    data() {
        return {
            localErrorMessage: undefined
        };
    },
    methods: {
        ...mapActions("functionality", {
            findAllApplications: "findAllApplications"
        }),
        getAppParamValue(appId, param) {
            const brandAccess = this.brand.brandAccesses.find(ba => ba.application === appId);
            const paramApp = brandAccess.applicationParameters.find(ap => ap.parameter.id === param.id);
            if (paramApp) {
                if (param.type === "boolean") {
                    return paramApp.value === 1 || paramApp.value === "1" || paramApp.value === true;
                } else {
                    return paramApp.value;
                }
            }
            return null;
        },
        updateParamValue(event, app, param) {
            const brandAccess = this.brand.brandAccesses.find(ba => ba.application === app.value);
            const index = brandAccess.applicationParameters.findIndex(ap => ap.parameter.id === param.id);
            if (index > -1) {
                brandAccess.applicationParameters.splice(brandAccess.applicationParameters.findIndex(ap => ap.parameter.id === param.id), 1);
            }
            if (param.type === "boolean") {
                brandAccess.applicationParameters.push({ parameter: { id: param.id }, value: event.target.checked ? 1 : 0 });
            } else {
                brandAccess.applicationParameters.push({ parameter: { id: param.id }, value: event.target.value });
            }
        },
        selectValue(value, e) {
            const brandAccess = this.brand.brandAccesses.find(ba => ba.application === value);
            if (e.target.checked) {
                if (brandAccess) {
                    brandAccess.active = true;
                } else {
                    this.brand.brandAccesses.push({ application: value, active: true, applicationParameters: [] });
                }
            } else {
                const brandAccess = this.brand.brandAccesses.find(ba => ba.application === value);
                if (brandAccess) {
                    brandAccess.active = false;
                }
            }
        },
        updateGlobalValue() {
            if (this.allApplicationsAreSelected) {
                this.brand.brandAccesses.map(ba => (ba.active = false));
            } else {
                this.brand.brandAccesses = this.applicationsUsable.map(au => {
                    const brandAccess = this.brand.brandAccesses.find(ba => ba.application === au.value);
                    if (brandAccess) {
                        brandAccess.active = true;
                        return brandAccess;
                    } else {
                        return { application: au.value, active: true, applicationParameters: [] };
                    }
                });
            }
        },
        applicationSelected(id) {
            return this.brand.brandAccesses.some(ba => ba.application === id);
        },
        applicationActive(id) {
            const brandAccess = this.brand.brandAccesses.find(ba => ba.application === id);
            if (brandAccess) {
                return brandAccess.active;
            }
            return false;
        }
    },
    computed: {
        ...mapGetters("functionality", {
            getAllApplications: "getAllApplications"
        }),
        error() {
            return this.errorMessage || this.localErrorMessage;
        },
        applications() {
            return (
                (this.getAllApplications &&
                    this.getAllApplications.map(app => ({
                        value: app.id,
                        logo: Globals.getBaseUrl() + app.pathImg,
                        label: this.getTranslate(app.terms.name),
                        readonly: app.id === CATALOG.id,
                        parameters: app.parameters
                    }))) ||
                []
            );
        },
        allApplicationsAreSelected() {
            return this.brand.brandAccesses.filter(ba => ba.active).length === this.applicationsUsable.length;
        },
        readonlyApplications() {
            return this.applications.filter(application => application.readonly);
        },
        applicationsUsable() {
            return this.applications.filter(application => !application.readonly);
        }
    },
    created() {
        JustDigits();
        if (!this.getAllApplications) {
            this.tryLoad(() => this.findAllApplications());
        }
    }
};
</script>

<style scoped lang="scss">
@import "../../../../assets/css/themes";
.title {
    @extend %primary-text;
}
.app-title {
    font-family: "Oswald", sans-serif;
    font-size: 18px;
}
.content-applications {
    @extend %primary-text;
    padding: 20px;
}
.container-parameters {
    margin-top: 5px;
    margin-bottom: 10px;
    background-color: #f4f4f4;
    padding-top: 10px;
    padding-bottom: 10px;
    .parameter {
        padding-top: 5px;
        padding-bottom: 5px;
        .input-number {
            max-width: 50px;
        }
        .input-text {
            max-width: 100px;
        }
        span {
            text-transform: initial;
        }
    }
}
</style>



// WEBPACK FOOTER //
// BrandAccess.vue?6542619a